import { downloadCsv, downloadCsvMatrix, type CsvCell, type CsvColumn } from "@/lib/csv";
import { formatFullDate } from "@/lib/format";
import { editionLabel } from "@/lib/license";
import type { LicenseCode } from "@/pages/admin/tools/hooks/useLicenseTools";

const CODE_STATUS_LABELS: Record<string, string> = {
  unused: "未使用",
  disabled: "已停用",
  used: "已使用",
};

const columns: CsvColumn<LicenseCode>[] = [
  { key: "code", label: "激活码", value: (row) => row.code },
  { key: "product_name", label: "适用产品", value: (row) => row.product_name ?? "" },
  { key: "edition", label: "版本类型", value: (row) => (row.edition ? editionLabel(row.edition) : "") },
  { key: "seat_count", label: "授权席位", value: (row) => row.seat_count },
  { key: "max_activations", label: "最大激活数", value: (row) => row.max_activations },
  { key: "status", label: "状态", value: (row) => CODE_STATUS_LABELS[row.status] ?? row.status },
  { key: "batch_id", label: "批次", value: (row) => row.batch_id ?? "" },
  { key: "note", label: "备注", value: (row) => row.note ?? "" },
  { key: "created_at", label: "生成时间", value: (row) => formatFullDate(row.created_at) },
];

export function codeStatusLabel(status: string): string {
  return CODE_STATUS_LABELS[status] ?? status;
}

// 单个批次的明细汇总：某批次里未核销 / 已核销 / 已停用各多少，并附逐条激活码明细，
// 供导出留痕「按批次下钻」展开查看该次导出实际涉及了哪些码。
export interface BatchCodeItem {
  code: string;
  status: string;
  productName: string;
  usedAt: string | null;
}

export interface BatchDetail {
  batchId: string;
  productName: string;
  unused: number;
  used: number;
  disabled: number;
  total: number;
  items: BatchCodeItem[];
}

export function summarizeBatchDetail(codes: LicenseCode[], batchId: string): BatchDetail {
  const scoped = codes.filter((row) => (row.batch_id || "(无批次)") === batchId);
  const detail: BatchDetail = {
    batchId,
    productName: scoped.find((row) => row.product_name)?.product_name ?? "",
    unused: 0,
    used: 0,
    disabled: 0,
    total: scoped.length,
    items: scoped.map((row) => ({
      code: row.code,
      status: row.status,
      productName: row.product_name ?? "",
      usedAt: row.used_at,
    })),
  };
  scoped.forEach((row) => {
    if (row.status === "used") detail.used += 1;
    else if (row.status === "disabled") detail.disabled += 1;
    else detail.unused += 1;
  });
  return detail;
}

export function downloadCodeList(codes: LicenseCode[], scope: string): number {
  const stamp = new Date().toISOString().slice(0, 10);
  downloadCsv(`原始激活码_${scope}_${stamp}.csv`, columns, codes);
  return codes.length;
}

// 纯文本导出：每行一个激活码，方便直接粘贴到分发邮件或导入打包工具。
export function downloadCodeListAsText(codes: LicenseCode[], scope: string): number {
  const stamp = new Date().toISOString().slice(0, 10);
  const rows = codes.map((row) => [row.code]);
  downloadCsvMatrix(`原始激活码_${scope}_${stamp}.txt.csv`, rows);
  return codes.length;
}

// 按批次汇总核销情况：用于发码方逐批对账（已核销 / 未核销 / 已停用）。
export interface BatchStat {
  batchId: string;
  productName: string;
  note: string;
  total: number;
  used: number;
  unused: number;
  disabled: number;
  redeemedRate: number;
  createdAt: string | null;
  lastUsedAt: string | null;
}

export function computeBatchStats(codes: LicenseCode[]): BatchStat[] {
  const map = new Map<string, BatchStat>();
  codes.forEach((row) => {
    const key = row.batch_id || "(无批次)";
    let stat = map.get(key);
    if (!stat) {
      stat = {
        batchId: key,
        productName: row.product_name ?? "",
        note: row.note ?? "",
        total: 0,
        used: 0,
        unused: 0,
        disabled: 0,
        redeemedRate: 0,
        createdAt: row.created_at,
        lastUsedAt: null,
      };
      map.set(key, stat);
    }
    stat.total += 1;
    if (!stat.productName && row.product_name) stat.productName = row.product_name;
    if (!stat.note && row.note) stat.note = row.note;
    if (row.status === "used") {
      stat.used += 1;
      if (row.used_at && (!stat.lastUsedAt || row.used_at > stat.lastUsedAt)) stat.lastUsedAt = row.used_at;
    } else if (row.status === "disabled") {
      stat.disabled += 1;
    } else {
      stat.unused += 1;
    }
    if (row.created_at && (!stat.createdAt || row.created_at < stat.createdAt)) stat.createdAt = row.created_at;
  });

  const list = Array.from(map.values());
  list.forEach((stat) => {
    stat.redeemedRate = stat.total > 0 ? Math.round((stat.used / stat.total) * 100) : 0;
  });
  return list.sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? ""));
}

export function downloadBatchStats(stats: BatchStat[]): number {
  const stamp = new Date().toISOString().slice(0, 10);
  const header = ["批次", "适用产品", "备注", "生成总数", "已核销", "未核销", "已停用", "核销率", "生成时间", "最近核销时间"];
  const rows = stats.map((stat) => [
    stat.batchId,
    stat.productName,
    stat.note,
    stat.total,
    stat.used,
    stat.unused,
    stat.disabled,
    `${stat.redeemedRate}%`,
    stat.createdAt ? formatFullDate(stat.createdAt) : "",
    stat.lastUsedAt ? formatFullDate(stat.lastUsedAt) : "",
  ]);
  downloadCsvMatrix(`激活码核销统计_${stamp}.csv`, [header, ...rows]);
  return stats.length;
}

// 按批次导出「尚未核销」的激活码清单：发码方存档或批量导入邮件系统时，只需拿还没发完的那部分。
export function downloadBatchRemainder(codes: LicenseCode[], batchId: string): number {
  const list = codes.filter((row) => (row.batch_id || "(无批次)") === batchId && row.status === "unused");
  const stamp = new Date().toISOString().slice(0, 10);
  const safeBatch = (batchId || "batch").replace(/[^\w.-]/g, "_");
  const header = ["激活码", "适用产品", "版本类型", "授权席位", "最大激活数", "备注", "生成时间"];
  const rows = list.map((row) => [
    row.code,
    row.product_name ?? "",
    row.edition ? editionLabel(row.edition) : "",
    row.seat_count,
    row.max_activations,
    row.note ?? "",
    row.created_at ? formatFullDate(row.created_at) : "",
  ]);
  downloadCsvMatrix(`未核销激活码清单_${safeBatch}_${stamp}.csv`, [header, ...rows]);
  return list.length;
}

// 「批次对账包」：把同一批次的「未核销」与「已核销」两段清单合到一个文件里，
// 一次导出即可看到整批的发放与核销现状（两段表，段间用空行分隔）。
export function downloadBatchReconcile(
  codes: LicenseCode[],
  batchId: string,
  productName: string,
): { unused: number; used: number } {
  const scoped = codes.filter((row) => (row.batch_id || "(无批次)") === batchId);
  const unused = scoped.filter((row) => row.status === "unused");
  const used = scoped.filter((row) => row.status === "used");
  const stamp = new Date().toISOString().slice(0, 10);
  const safeBatch = (batchId || "batch").replace(/[^\w.-]/g, "_");

  const rows: CsvCell[][] = [];
  rows.push([`批次对账包 · 批次 ${batchId}${productName ? ` · ${productName}` : ""}`]);
  rows.push([`导出时间：${formatFullDate(new Date().toISOString())}`]);
  rows.push([]);
  rows.push([`【未核销激活码】共 ${unused.length} 个`]);
  rows.push(["激活码", "适用产品", "版本类型", "授权席位", "最大激活数", "备注", "生成时间"]);
  unused.forEach((row) => {
    rows.push([
      row.code,
      row.product_name ?? "",
      row.edition ? editionLabel(row.edition) : "",
      row.seat_count,
      row.max_activations,
      row.note ?? "",
      row.created_at ? formatFullDate(row.created_at) : "",
    ]);
  });
  rows.push([]);
  rows.push([`【已核销许可证】共 ${used.length} 个`]);
  rows.push(["激活码", "适用产品", "版本类型", "授权席位", "核销时间"]);
  used.forEach((row) => {
    rows.push([
      row.code,
      row.product_name ?? "",
      row.edition ? editionLabel(row.edition) : "",
      row.seat_count,
      row.used_at ? formatFullDate(row.used_at) : "",
    ]);
  });

  downloadCsvMatrix(`批次对账包_${safeBatch}_${stamp}.csv`, rows);
  return { unused: unused.length, used: used.length };
}

// 「全部批次对账包」：一次把所有批次的「未核销 + 已核销」明细打进同一个文件，
// 每个批次一段（批次标题 + 明细类型列），适合整体归档或交给上级一次性核对。
export function downloadAllBatchesReconcile(
  codes: LicenseCode[],
  stats: BatchStat[],
): { batches: number; unused: number; used: number } {
  const stamp = new Date().toISOString().slice(0, 10);
  const rows: CsvCell[][] = [];
  rows.push(["全部批次对账包"]);
  rows.push([`导出时间：${formatFullDate(new Date().toISOString())}`]);
  rows.push([`批次数量：${stats.length}`]);

  let totalUnused = 0;
  let totalUsed = 0;
  stats.forEach((stat, index) => {
    const scoped = codes.filter((row) => (row.batch_id || "(无批次)") === stat.batchId);
    const unused = scoped.filter((row) => row.status === "unused");
    const used = scoped.filter((row) => row.status === "used");
    totalUnused += unused.length;
    totalUsed += used.length;

    rows.push([]);
    rows.push([
      `【批次 ${index + 1}/${stats.length}】${stat.batchId}${stat.productName ? ` · ${stat.productName}` : ""}`,
    ]);
    rows.push([`未核销 ${unused.length} · 已核销 ${used.length}`]);
    rows.push(["明细类型", "激活码", "适用产品", "版本类型", "授权席位", "时间"]);
    unused.forEach((row) => {
      rows.push([
        "未核销",
        row.code,
        row.product_name ?? "",
        row.edition ? editionLabel(row.edition) : "",
        row.seat_count,
        row.created_at ? formatFullDate(row.created_at) : "",
      ]);
    });
    used.forEach((row) => {
      rows.push([
        "已核销",
        row.code,
        row.product_name ?? "",
        row.edition ? editionLabel(row.edition) : "",
        row.seat_count,
        row.used_at ? formatFullDate(row.used_at) : "",
      ]);
    });
  });

  downloadCsvMatrix(`全部批次对账包_${stamp}.csv`, rows);
  return { batches: stats.length, unused: totalUnused, used: totalUsed };
}

// 把一个批次的「下钻明细」直接落成 CSV：用于导出留痕展开某次导出后，一键把看到的
// 逐条激活码明细（激活码 / 状态 / 产品 / 核销时间）存下来，作为那次导出的证据快照。
export function downloadBatchDetailCsv(detail: BatchDetail): number {
  const stamp = new Date().toISOString().slice(0, 10);
  const safeBatch = (detail.batchId || "batch").replace(/[^\w.-]/g, "_");
  const rows: CsvCell[][] = [
    [`批次明细快照 · 批次 ${detail.batchId}${detail.productName ? ` · ${detail.productName}` : ""}`],
    [`导出时间：${formatFullDate(new Date().toISOString())}`],
    [`未核销 ${detail.unused} · 已核销 ${detail.used} · 已停用 ${detail.disabled} · 合计 ${detail.total}`],
    [],
    ["激活码", "状态", "适用产品", "核销时间"],
  ];
  detail.items.forEach((item) => {
    rows.push([
      item.code,
      CODE_STATUS_LABELS[item.status] ?? item.status,
      item.productName,
      item.usedAt ? formatFullDate(item.usedAt) : "",
    ]);
  });
  downloadCsvMatrix(`批次明细_${safeBatch}_${stamp}.csv`, rows);
  return detail.items.length;
}